import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import { useGetIncomingPostbackStepLogQuery } from "api/TenantSlug/StepLog/hooks";
// import { RunHistoryLink } from "components/CC/RunHistory/StepDetails/RunHistoryLink";
import { StepDetailRenderer } from "components/CC/RunHistory/StepDetails/StepDetailRenderer";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList";
import { QueryState } from "components/UI/QueryState/QueryState";
import { useStepTypeLabel } from "components/UI/Selects/StepTypeSelectSearchable/useStepTypeLabel";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { ApiResponseBodyCell } from "components/UI/Table/UI/ApiResponseBodyCell/ApiResponseBodyCell";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { PermissionEnum } from "types/enums";
import { DrawerId } from "types/models";

import StepLogIncomingPostbackData = App.Data.StepLogIncomingPostbackData;

interface IncomingPostbackStepLogDetailDrawerProps {
    logId: string;
}

export const IncomingPostbackStepLogDetailDrawer: FC<IncomingPostbackStepLogDetailDrawerProps> = ({ logId }) => {
    const { t } = usePaldockTranslation("drawers.incoming-postback-step-log-detail");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const getStepTypeLabel = useStepTypeLabel();
    const drawerId = DrawerId.IncomingPostbackStepLogDetail;
    const permissionsUtils = usePermission();
    const canViewForeign = permissionsUtils.hasPermission(PermissionEnum.ConnectionSequenceStepLogForeign);

    const getIncomingPostbackStepLogQuery = useGetIncomingPostbackStepLogQuery(logId, {
        filter: { include: "incomingPostback,commission,conversionState,owner,advertiser,offer,stepRun" },
    });
    const log = getIncomingPostbackStepLogQuery.data?.incomingPostbackLog;

    const logList = log
        ? PropertiesList.create<StepLogIncomingPostbackData>(log, [
              {
                  term: tG("date"),
                  description: ({ stepRun }) =>
                      stepRun?.started_at ? <DateTimeCell value={stepRun.started_at} /> : <EmptyCell />,
              },
              {
                  term: tG("origin_id"),
                  description: ({ origin_eid }) =>
                      origin_eid ? <ShortenedIdWithCopy charactersBetweenDots={35} id={origin_eid} /> : <EmptyCell />,
              },
              {
                  term: tG("event_id"),
                  description: ({ eid }) =>
                      eid ? <ShortenedIdWithCopy charactersBetweenDots={35} id={eid} /> : <EmptyCell />,
              },
              {
                  term: tG("external_id"),
                  description: ({ advertiser_custom_id }) =>
                      advertiser_custom_id ? (
                          <ShortenedIdWithCopy charactersBetweenDots={35} id={advertiser_custom_id} />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: t("fields.commission"),
                  description: ({ commission }) => <EntityLinkCell.Commission entity={commission} />,
              },
              {
                  term: tG("transaction"),
                  description: ({ conversionState }) => conversionState?.type ?? <EmptyCell />,
              },
              {
                  term: tG("affiliate"),
                  description: ({ owner }) => <EntityLinkCell.Owner entity={owner} />,
              },
              ...(canViewForeign
                  ? [
                        {
                            term: tG("advertiser"),
                            description: ({ advertiser }: StepLogIncomingPostbackData) => (
                                <EntityLinkCell.Advertiser entity={advertiser} />
                            ),
                        },
                    ]
                  : []),
              {
                  term: tG("offer"),
                  description: ({ offer }) => <EntityLinkCell.Offer entity={offer} />,
              },
              {
                  term: tG("name"),
                  description: ({ incomingPostback }) => <EntityLinkCell.IncomingPostback entity={incomingPostback} />,
              },
              {
                  term: t("fields.step"),
                  description: ({ stepRun }) => stepRun?.title ?? <EmptyCell />,
              },
              {
                  term: t("fields.type"),
                  description: ({ stepRun }) => (stepRun?.type ? getStepTypeLabel(stepRun.type) : <EmptyCell />),
              },
              {
                  term: tG("status"),
                  description: ({ http_status_code }) => http_status_code ?? <EmptyCell />,
              },
              {
                  term: tG("state"),
                  description: ({ state }) =>
                      state ? (
                          <Tag icon={<CheckCircleOutlined />} color="green">
                              {tGL("ok")}
                          </Tag>
                      ) : (
                          <Tag icon={<CloseCircleOutlined />} color="red">
                              {tGL("fail")}
                          </Tag>
                      ),
              },
              {
                  term: t("fields.detail"),
                  description: ({ message, id }) =>
                      message != null ? (
                          <ApiResponseBodyCell response={{ body: message }} id={`message-${id}`} lines={10} />
                      ) : (
                          <EmptyCell />
                      ),
              },
          ])
        : null;

    return (
        <Drawer
            drawerId={drawerId}
            title={<DrawerTitle mainTitle={t("title")} />}
            searchParamsToClean={[`${drawerId}-id`]}
            // extraButtons={<RunHistoryLink />}
        >
            {!log ? (
                <QueryState
                    isLoading={getIncomingPostbackStepLogQuery.isLoading}
                    isError={
                        getIncomingPostbackStepLogQuery.isError || (!getIncomingPostbackStepLogQuery.isLoading && !log)
                    }
                    errorKey="errors.something_went_wrong"
                />
            ) : (
                <div className="flex flex-col gap-4">
                    <DrawerContent className="flex flex-col gap-4 p-4">
                        <DrawerCollapsible title={t("sections.log")}>
                            <PropertiesList list={logList ?? []} />
                        </DrawerCollapsible>
                        {log.stepRun && (
                            <StepDetailRenderer
                                stepRun={log.stepRun}
                                logContext={{
                                    httpStatusCode: log.http_status_code,
                                    message: log.message,
                                    conversionState: log.conversionState,
                                }}
                            />
                        )}
                    </DrawerContent>
                </div>
            )}
        </Drawer>
    );
};
